import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BoxIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, CircleDollarSignIcon, FilterIcon, SearchIcon, XIcon } from 'lucide-react';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { NisoloProductTable } from './NisoloProductTable';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
type UnitStatus = 'Active' | 'Sold' | 'Inactive';
type Channel = 'Manymoons' | 'Whatnot' | 'Collective' | 'Circular Shop';
type UnitRow = {
  id: number;
  name: string;
  size: string;
  condition: 'Rescue' | 'Resale' | 'Damaged';
  status: UnitStatus;
  received: number;
  sold: number;
  dateLive: string;
  channel?: Channel;
  msrp: number;
  currentPrice: number;
  imageUrl: string;
};
const productSeeds = [{
  name: 'Caroline Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 398
}, {
  name: 'Sydney Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Sophia Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 548
}, {
  name: 'Katherine Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 598
}, {
  name: 'Sarah Bird Mini Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Daisy Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 568
}, {
  name: 'Desirae Midi Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/870c32c0-b80e-4da5-9260-ac098cc1a3bb.jpg',
  msrp: 578
}];
const sizes = ['X-Small', 'Small', 'Medium', 'Large', 'X-Large'];
const channels: Channel[] = ['Manymoons', 'Whatnot', 'Collective', 'Circular Shop'];
const PAGE_SIZE = 12;
function createMockUnits() {
  return Array.from({
    length: 150
  }, (_, index): UnitRow => {
    const product = productSeeds[index % productSeeds.length];
    const received = 1 + index % 7;
    const sold = index % 5 === 0 ? received : index % 3 === 0 ? 1 : 0;
    const status: UnitStatus = sold === received ? 'Sold' : index % 13 === 0 ? 'Inactive' : 'Active';
    const condition = index % 19 === 0 ? 'Damaged' : index % 11 === 0 ? 'Resale' : 'Rescue';
    return {
      id: index + 1,
      name: product.name,
      size: sizes[index % sizes.length],
      condition,
      status,
      received,
      sold,
      dateLive: index % 4 === 0 ? 'Jun 30, 2026' : index % 3 === 0 ? 'Jun 24, 2026' : 'Dec 18, 2025',
      channel: sold > 0 ? channels[index % channels.length] : undefined,
      msrp: product.msrp,
      currentPrice: Math.round(product.msrp * (condition === 'Rescue' ? 0.28 : condition === 'Resale' ? 0.45 : 0.1)),
      imageUrl: product.imageUrl
    };
  });
}
const unitRows = createMockUnits();
export function UnitsDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UnitStatus | 'All'>('All');
  const [page, setPage] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState<UnitRow | null>(null);
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    inventory: nisolo,
    loading: nisoloLoading,
    error: nisoloError
  } = useNisoloProducts(isNisolo);
  const {
    data: nisoloSubmissions
  } = useNisoloSubmissions(isNisolo);
  const {
    data: nisoloOrders
  } = useNisoloOrders(isNisolo);
  const hasData = selectedOwner === 'Eddy' || isNisolo && Boolean(nisolo);
  const nisoloRows = useMemo<UnitRow[]>(() => {
    if (!nisolo) return [];
    return nisolo.products.flatMap((product, productIndex) => (product.sizes.length > 0 ? product.sizes : ['One size']).map((size, sizeIndex): UnitRow => {
      const perSize = Math.max(1, Math.round(product.units / Math.max(1, product.sizes.length || 1)));
      const unitMsrp = product.variants > 0 ? Math.round(product.msrp / Math.max(1, product.units || 1)) : 0;
      return {
        id: productIndex * 100 + sizeIndex + 1,
        name: product.title,
        size,
        condition: 'Rescue',
        status: product.status.toLocaleLowerCase() === 'active' ? 'Active' : 'Inactive',
        received: perSize,
        sold: 0,
        dateLive: 'Feb 15, 2026',
        msrp: unitMsrp || product.price,
        currentPrice: product.price,
        imageUrl: product.image
      };
    }));
  }, [nisolo]);
  const sourceRows = isNisolo ? nisoloRows : unitRows;
  const summary = isNisolo ? {
    received: (nisoloSubmissions?.totalReceived ?? 0).toLocaleString('en-US'),
    receivedDetail: `Across ${(nisolo?.totalProducts ?? 0).toLocaleString('en-US')} products`,
    sold: (nisoloOrders?.units ?? 0).toLocaleString('en-US'),
    sellThrough: nisoloSubmissions && nisoloSubmissions.totalReceived > 0 ? `${((nisoloOrders?.units ?? 0) / nisoloSubmissions.totalReceived * 100).toFixed(1)}%` : '0%',
    live: (nisolo?.liveUnits ?? 0).toLocaleString('en-US')
  } : {
    received: '4,758',
    receivedDetail: 'Across 320 products',
    sold: '4,010',
    sellThrough: '84.3%',
    live: '667'
  };
  const filteredUnits = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sourceRows.filter(unit => {
      const matchesQuery = !normalizedQuery || `${unit.name} ${unit.size}`.toLowerCase().includes(normalizedQuery);
      const matchesStatus = statusFilter === 'All' || unit.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [sourceRows, query, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredUnits.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredUnits.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setFilter = (filter: UnitStatus | 'All') => {
    setStatusFilter(filter);
    setPage(1);
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="units-heading" data-id="element-1897" data-mp="1msob0o0fpfci">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1898" data-mp="1qt86ln5d0awj">
        <div data-id="element-1899" data-mp="2ekdo06qy7b8y">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1900" data-mp="2ejvtpfnuj73o">
            <BoxIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1901" data-mp="2jkjh7rcv95t5" />
            Units
          </span>
          <h1 id="units-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1902" data-mp="25oxwhwdo186j">Individual units</h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1903" data-mp="2xigekpz59g8v">Track individual units from receipt through sale, including condition, price, and the channel where each item sold.</p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1904" data-mp="qswc4jmxx8bn">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1905" data-mp="lul48eg7h7w2" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      {hasData ? <>
          <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Units summary" data-id="element-1906" data-mp="vlpkdhhmaysh">
            <UnitMetric label="Received units" value={summary.received} detail={summary.receivedDetail} data-id="element-1907" data-mp="7vv5yvhnh264" />
            <UnitMetric label="Units sold" value={summary.sold} detail="All recovery channels" data-id="element-1908" data-mp="2xjuyhfmclmb" />
            <UnitMetric label="Sell-through" value={summary.sellThrough} detail="Including replenishment cycles" data-id="element-1909" data-mp="huug6ii2d5v8" />
            <UnitMetric label="Live units" value={summary.live} detail="Currently available" data-id="element-1910" data-mp="csvc16ei24x8" />
          </section>

          <section className="mt-5 overflow-hidden rounded-3xl border border-ink/10 bg-paper shadow-sm" aria-labelledby="unit-table-heading" data-id="element-1911" data-mp="1ta9bziyjdror">
            <div className="flex flex-col gap-3 border-b border-ink/10 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-1912" data-mp="3hsu1ipbqka5u">
              <div data-id="element-1913" data-mp="3mrf0klf94nsp">
                <h2 id="unit-table-heading" className="text-lg font-semibold" data-id="element-1914" data-mp="3mosxoz3b2wgt">Unit ledger</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1915" data-mp="3rof5uu4a1mir">Select a row to inspect its recovery history.</p>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-1916" data-mp="5axe7z5oki9u">
                {(['All', 'Active', 'Sold', 'Inactive'] as const).map(filter => <button key={filter} type="button" onClick={() => setFilter(filter)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === filter ? 'bg-[#BB5E40] text-cream' : 'bg-cream text-ink/60 hover:bg-ink/5'}`} data-id="element-1917" data-mp="9ao3fyfk6qc9">{filter}</button>)}
              </div>
            </div>
            <div className="border-b border-ink/10 px-5 py-4 sm:px-6" data-id="element-1918" data-mp="2g51me0qdefmg">
              <label className="sr-only" htmlFor="unit-search" data-id="element-1919" data-mp="2l2bj81g39tkb">Search units</label>
              <div className="relative" data-id="element-1920" data-mp="2dj2bsdr584xw">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1921" data-mp="2dk3osnrpz1i9" />
                <input id="unit-search" type="search" value={query} onChange={event => {
              setQuery(event.target.value);
              setPage(1);
            }} placeholder="Search by product name or size" className="w-full rounded-xl border border-ink/10 bg-cream px-10 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40]/40 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-1922" data-mp="23ro7zts5siyy" />
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-1923" data-mp="2mhnqtyxep1tx">
              <table className="w-full min-w-[1000px] text-left text-xs" data-id="element-1924" data-mp="2mkrzp9ltglqx">
                <thead className="bg-cream/50 text-ink/50" data-id="element-1925" data-mp="2hjm9bifmpakk">
                  <tr data-id="element-1926" data-mp="1b421acnmwy7v">
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1927" data-mp="1b5d7ej4jyi25">Status</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1928" data-mp="163xmcr2dkbjr">Product</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1929" data-mp="z032nsf3krgj">Condition</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1930" data-mp="u29yf4xcu7iw">Received</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1931" data-mp="18r66h1zevkji">Sold</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1932" data-mp="1407n1tj4m6c1">Sell-through</th>
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1933" data-mp="1hrcew9g95ubt">Sold on</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-1934" data-mp="1dopkq1w3zxbg">
                  {visibleRows.map(unit => <UnitTableRow key={unit.id} unit={unit} onSelect={() => setSelectedUnit(unit)} data-id="element-1935" data-mp="1rps76fr0n9mw" />)}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-ink/10 px-5 py-3 text-xs text-ink/55 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-1936" data-mp="1momeuj19podw">
              <span data-id="element-1937" data-mp="3oj3r2ebzz84u">Showing {filteredUnits.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredUnits.length)}` : '0'} of {filteredUnits.length} units</span>
              <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-1938" data-mp="28xjdgf4okvab">
                <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={currentPage === 1} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Previous units page" data-id="element-1939" data-mp="1uaq7i0h1x1us"><ChevronLeftIcon className="h-4 w-4" data-id="element-1940" data-mp="1l7p4i8nhfqvc" /></button>
                <span data-id="element-1941" data-mp="1q8mu36atnn6v"><strong className="font-semibold text-ink" data-id="element-1942" data-mp="1q8mrqzncnb3p">{currentPage}</strong> of {pageCount}</span>
                <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={currentPage === pageCount} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Next units page" data-id="element-1943" data-mp="1bc5l2lq3uknh"><ChevronRightIcon className="h-4 w-4" data-id="element-1944" data-mp="35nak1cq8y8wh" /></button>
              </div>
            </div>
          </section>

          {isNisolo && <section className="mt-5" aria-label="Product inventory" data-id="element-1945" data-mp="3982jyfrv0l0y">
              <NisoloProductTable products={nisolo?.products ?? []} loading={nisoloLoading} error={nisoloError} data-id="element-1946" data-mp="3487tuefwrzgy" />
            </section>}
        </> : <section className="mt-8 rounded-3xl border border-dashed border-ink/15 bg-paper px-6 py-16 text-center" data-id="element-1947" data-mp="2zaxw8bid8hi1">
          <BoxIcon className="mx-auto h-6 w-6 text-[#BB5E40]" aria-hidden="true" data-id="element-1948" data-mp="2v9mbokecmmgp" />
          <h2 className="mt-4 text-lg font-semibold" data-id="element-1949" data-mp="3rzmzag2zewwf">Select an owner to view units</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-1950" data-mp="3mz7onwa343i5">Unit-level inventory will appear here with its current state, recovery progress, and channel attribution.</p>
        </section>}

      <AnimatePresence data-id="element-1951" data-mp="3e41budcecl64">
        {selectedUnit && <UnitDetailDrawer unit={selectedUnit} onClose={() => setSelectedUnit(null)} data-id="element-1952" data-mp="eiw2xvb453h1" />}
      </AnimatePresence>
    </div>;
}
function UnitMetric({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1953" data-mp="1sg8i817lrwqh">
    <BoxIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1954" data-mp="1xcyxwnfhwqbd" />
    <p className="mt-4 text-sm text-ink/55" data-id="element-1955" data-mp="21fc1b56rsc4z">{label}</p>
    <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1956" data-mp="21ekdwhnbtkps">{value}</p>
    <p className="mt-1 text-xs text-ink/50" data-id="element-1957" data-mp="26f85b4fgw3kj">{detail}</p>
  </article>;
}
function UnitTableRow({
  unit,
  onSelect
}: {
  unit: UnitRow;
  onSelect: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  const statusStyle = unit.status === 'Sold' ? 'bg-[#DCE5FA] text-[#40569E]' : unit.status === 'Inactive' ? 'bg-ink/10 text-ink/55' : 'bg-forest text-cream';
  const conditionStyle = unit.condition === 'Rescue' ? 'bg-[#A77ACA] text-cream' : unit.condition === 'Resale' ? 'bg-[#4D9CC7] text-cream' : 'bg-[#C97968] text-cream';
  const progressStyle = sellThrough === 100 ? 'bg-forest' : sellThrough > 0 ? 'bg-[#4D9CC7]' : 'bg-ink/15';
  return <tr onClick={onSelect} className="cursor-pointer transition-colors hover:bg-[#BB5E40]/[0.035] focus-within:bg-[#BB5E40]/[0.035]" data-id="element-1958" data-mp="2lflte8xce4jp">
    <td className="px-5 py-2.5 sm:px-6" data-id="element-1959" data-mp="2ggr9t4rjwbyj"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`} data-id="element-1960" data-mp="2v66z0gbfo9c4">{unit.status}</span></td>
    <th scope="row" className="px-3 py-2.5 text-left font-medium" data-id="element-1961" data-mp="2q6kcfg9ptgqn"><span className="flex items-center gap-2.5" data-id="element-1962" data-mp="2q96rs22ul0jf"><img src={unit.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" data-id="element-1963" data-mp="22gpz6xlhhq5v" /><span data-id="element-1964" data-mp="1xig6ctn8f1nf"><span className="block text-ink" data-id="element-1965" data-mp="1xiohoh3phywi">{unit.name}</span><span className="mt-0.5 block font-normal text-ink/50" data-id="element-1966" data-mp="1xjzkoepbv2mk">{unit.size}</span></span></span></th>
    <td className="px-3 py-2.5" data-id="element-1967" data-mp="2cha5ienv1kuy"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${conditionStyle}`} data-id="element-1968" data-mp="2cdcivj22qb6b">{unit.condition}</span></td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-1969" data-mp="3num4ab4rjda9">{unit.received}</td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-1970" data-mp="3ivrh6wzrj2kc">{unit.sold}</td>
    <td className="px-3 py-2.5" data-id="element-1971" data-mp="3iy3yd7xp58wc"><div className="flex min-w-32 items-center gap-2" data-id="element-1972" data-mp="3qer2x9ap566v"><div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-1973" data-mp="3qdz5dsxjgydy"><div className={`h-full rounded-full ${progressStyle}`} style={{
            width: `${sellThrough}%`
          }} data-id="element-1974" data-mp="3ven470s0ejiq" /></div><span className="w-9 text-right tabular-nums text-ink/55" data-id="element-1975" data-mp="3hfdnlzzw5ne8">{sellThrough}%</span></div></td>
    <td className="px-5 py-2.5 sm:px-6" data-id="element-1976" data-mp="3lffjttmuxom4">{unit.channel ? <ChannelBadge channel={unit.channel} data-id="element-1977" data-mp="3lia51ndp3dgl" /> : <span className="text-ink/40" data-id="element-1978" data-mp="czvw1a4ygyeg">—</span>}</td>
  </tr>;
}
function ChannelBadge({
  channel
}: {
  channel: Channel;
}) {
  const marks: Record<Channel, string> = {
    Manymoons: 'M',
    Whatnot: 'W',
    Collective: 'S',
    'Circular Shop': 'M'
  };
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-[#BB5E40]/8 py-1 pl-1 pr-2.5 text-xs font-semibold text-[#94452E]" data-id="element-1979" data-mp="47keyevqenb5"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#BB5E40] text-[10px] font-display text-cream" data-id="element-1980" data-mp="87b4ygdfozem">{marks[channel]}</span>{channel}</span>;
}
function UnitDetailDrawer({
  unit,
  onClose
}: {
  unit: UnitRow;
  onClose: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  return <>
    <motion.button type="button" aria-label="Close unit details" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-[1px]" data-id="element-1981" data-mp="16lepmikzb4li" />
    <motion.aside initial={{
      opacity: 0,
      x: 42
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 42
    }} transition={{
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-ink/10 bg-paper shadow-2xl" aria-label={`${unit.name} unit details`} data-id="element-1982" data-mp="wlnv4gwf4plf">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink/10 bg-paper/95 px-5 py-5 backdrop-blur sm:px-7" data-id="element-1983" data-mp="iseq0sfi6q8p"><h2 className="text-xl font-semibold tracking-tight" data-id="element-1984" data-mp="8wuxmg05e9op">{unit.name} <span className="text-ink/45" data-id="element-1985" data-mp="8yfre3en1oli">· {unit.size}</span></h2><button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/55 transition-colors hover:bg-cream hover:text-ink" aria-label="Close details" data-id="element-1986" data-mp="3ytejv2q2ad8"><XIcon className="h-5 w-5" data-id="element-1987" data-mp="3uy296dhrdijk" /></button></div>
      <div className="space-y-5 p-5 sm:p-7" data-id="element-1988" data-mp="3uwr5edobcasg">
        <div className="flex gap-4 rounded-2xl border border-ink/10 bg-cream/65 p-4" data-id="element-1989" data-mp="3rrgb4mwjo3b"><img src={unit.imageUrl} alt={`${unit.name} product`} className="h-24 w-20 rounded-xl object-cover" data-id="element-1990" data-mp="3pogjsvy6gle" /><div data-id="element-1991" data-mp="8ormdfjr4quf"><p className="text-sm font-semibold" data-id="element-1992" data-mp="8orkfa06ygrs">{unit.name}</p><p className="mt-1 text-sm text-ink/55" data-id="element-1993" data-mp="3799e0j8yneq0">{unit.size} · {unit.condition}</p><p className="mt-3 font-display text-2xl font-semibold" data-id="element-1994" data-mp="3c7b5nskgcmd3">${unit.currentPrice.toFixed(2)}</p><p className="text-xs text-ink/50" data-id="element-1995" data-mp="3h6ntafj5dxgq">Current recovery price</p></div></div>
        <DetailSection title="Inventory" data-id="element-1996" data-mp="3h4ksd0ggijx8"><DetailMetric label="Received" value={String(unit.received)} data-id="element-1997" data-mp="3lx48a42m9r57" /><DetailMetric label="Sold" value={String(unit.sold)} data-id="element-1998" data-mp="3lzgvowr58tpn" /><DetailMetric label="Current units on hand" value={String(unit.received - unit.sold)} data-id="element-1999" data-mp="2oirwlatkw5jy" /><DetailMetric label="Available for sale" value={String(unit.received - unit.sold)} data-id="element-2000" data-mp="2tgjmjzanxhli" /><div className="col-span-2 mt-1" data-id="element-2001" data-mp="bhvtpgledc82"><div className="flex items-center justify-between text-sm" data-id="element-2002" data-mp="bj6xhgeuejz6"><span className="font-medium text-ink/65" data-id="element-2003" data-mp="7gk1d3b52cw6">Sell-through</span><span className="font-semibold tabular-nums" data-id="element-2004" data-mp="7i4v4qpmprsz">{sellThrough}%</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-ink/10" data-id="element-2005" data-mp="lglcdbl4b0pd"><div className="h-full rounded-full bg-forest" style={{
                width: `${sellThrough}%`
              }} data-id="element-2006" data-mp="gh8fdy0j8d92" /></div></div></DetailSection>
        <DetailSection title="Sales" data-id="element-2007" data-mp="ughueud051bg"><DetailMetric label="MSRP" value={`$${unit.msrp.toFixed(2)}`} data-id="element-2008" data-mp="ufowmiw0tm5g" /><DetailMetric label="Starting price" value={`$${(unit.msrp * 0.5).toFixed(2)}`} data-id="element-2009" data-mp="qfo5bxntpggz" /><DetailMetric label="Current price" value={`$${unit.currentPrice.toFixed(2)}`} data-id="element-2010" data-mp="qdlgvi2bjved" /><DetailMetric label="Total sold" value={`$${(unit.sold * unit.currentPrice).toFixed(2)}`} data-id="element-2011" data-mp="1461iqfjhhndp" /><div className="col-span-2 rounded-xl bg-[#BB5E40]/5 p-3" data-id="element-2012" data-mp="z8i1r7veiwi2"><p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2013" data-mp="1e4h401vty2fa">Channel attribution</p>{unit.channel ? <div className="mt-2" data-id="element-2014" data-mp="196yuoadwhl6r"><ChannelBadge channel={unit.channel} data-id="element-2015" data-mp="197h28360jltz" /></div> : <p className="mt-2 text-sm text-ink/55" data-id="element-2016" data-mp="1srz4eo2d0avw">Not sold yet—channel will appear after the first sale.</p>}</div></DetailSection>
        <div className="flex items-center gap-2 rounded-xl border border-forest/15 bg-forest/5 p-3 text-sm text-ink/65" data-id="element-2017" data-mp="1ws9lowvlqnq2"><CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-2018" data-mp="1isqd62yvpad8" />Live since {unit.dateLive}</div>
      </div>
    </motion.aside>
  </>;
}
function DetailSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper" data-id="element-2019" data-mp="1du6qg0cgnujt"><h3 className="border-b border-ink/10 px-4 py-3 text-sm font-semibold" data-id="element-2020" data-mp="1dvq73s2147m4">{title}</h3><div className="grid grid-cols-2 gap-x-4 gap-y-5 p-4" data-id="element-2021" data-mp="104bpnpnrseh6">{children}</div></section>;
}
function DetailMetric({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-2022" data-mp="q4cgprnfum3s"><p className="text-xs text-ink/50" data-id="element-2023" data-mp="q4kq39kcr9a8">{label}</p><p className="mt-1 text-lg font-semibold tabular-nums text-ink" data-id="element-2024" data-mp="q5mc292wpg6q">{value}</p></div>;
}